import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { format, formatDistanceToNow, isPast, isWithinInterval, addDays } from 'date-fns';
import { CalendarClock, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, LoadingState, StatusBadge } from '@/components/common';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  maintenanceScheduleApi,
  MAINTENANCE_FREQUENCIES,
  type MaintenanceSchedule,
} from '@/api/services/maintenance-schedule.api';
import { getErrorMessage } from '@/api/client';

/**
 * One maintenance schedule — its cadence, what the job involves, and the parts
 * it will consume.
 *
 * A schedule is what work orders come from, and a work order has had its own
 * page all along. Reading the schedule behind it in a sheet meant the thing
 * being generated was linkable but the thing generating it was not.
 */
export default function MaintenanceScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<MaintenanceSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await maintenanceScheduleApi.getById(id);
      setSchedule(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/maintenance');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const generateWorkOrder = async () => {
    if (!schedule) return;
    try {
      const result = await maintenanceScheduleApi.generateWorkOrder(schedule.id);
      const created = result.data as { id: string; workOrderNumber: string };
      toast.success(`Work order ${created.workOrderNumber} created`);
      setGenerateOpen(false);
      navigate(`/work-orders/${created.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) return <LoadingState variant="detail" />;
  if (!schedule) return null;

  const due = new Date(schedule.nextDue);
  // The same three bands the list uses. Overdue maintenance is the reason to
  // open this page at all, so it gets the colour.
  const dueTone = isPast(due)
    ? ('destructive' as const)
    : isWithinInterval(due, { start: new Date(), end: addDays(new Date(), 7) })
      ? ('warning' as const)
      : ('default' as const);
  const dueLabel = isPast(due) ? 'Overdue' : dueTone === 'warning' ? 'Due soon' : 'On track';

  const frequency =
    MAINTENANCE_FREQUENCIES.find((f) => f.value === schedule.frequency)?.label ??
    schedule.frequency;
  const interval = schedule.intervalDays
    ? `${schedule.intervalDays} days`
    : schedule.intervalHours
      ? `${schedule.intervalHours} run hours`
      : null;
  const parts = schedule.requiredParts ?? [];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Maintenance', href: '/maintenance' },
            { label: schedule.name },
          ]}
          title={schedule.name}
          description={
            schedule.equipment
              ? `${schedule.equipment.name} · ${schedule.equipment.assetNumber}`
              : undefined
          }
          titleAdornment={
            <div className="flex gap-2">
              <StatusBadge status={schedule.status} />
              <StatusBadge status={schedule.priority} />
            </div>
          }
          actions={
            <Button onClick={() => setGenerateOpen(true)}>
              <Wrench className="mr-2 h-4 w-4" />
              Generate work order
            </Button>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard
                title="Cadence"
                icon={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Frequency', value: frequency },
                    { label: 'Interval', value: interval, when: !!interval },
                    { label: 'Next due', value: format(due, 'MMM d, yyyy') },
                    {
                      label: dueLabel,
                      value: formatDistanceToNow(due, { addSuffix: true }),
                      tone: dueTone,
                    },
                    {
                      label: 'Last performed',
                      value: schedule.lastPerformed
                        ? format(new Date(schedule.lastPerformed), 'MMM d, yyyy')
                        : 'Never',
                      // A schedule that has never run is worth noticing on a
                      // page about keeping to a cadence.
                      tone: schedule.lastPerformed ? 'default' : 'warning',
                    },
                    {
                      label: 'Estimated duration',
                      value: schedule.estimatedDuration
                        ? `${schedule.estimatedDuration} hours`
                        : null,
                      when: !!schedule.estimatedDuration,
                    },
                  ]}
                />
              </RailCard>

              {schedule.equipment && (
                <RailCard
                  title="Equipment"
                  icon={<Wrench className="h-4 w-4 text-muted-foreground" />}
                >
                  <DetailList
                    items={[
                      {
                        label: 'Asset',
                        value: (
                          <Link
                            to={`/equipment/${schedule.equipmentId}`}
                            className="underline-offset-4 hover:underline"
                          >
                            {schedule.equipment.assetNumber}
                          </Link>
                        ),
                      },
                      { label: 'Name', value: schedule.equipment.name },
                      {
                        label: 'Status',
                        value: schedule.equipment.status ? (
                          <StatusBadge status={schedule.equipment.status} />
                        ) : null,
                        when: !!schedule.equipment.status,
                      },
                      {
                        label: 'Condition',
                        value: schedule.equipment.condition,
                        when: !!schedule.equipment.condition,
                      },
                    ]}
                  />
                </RailCard>
              )}
            </>
          }
          main={
            <div className="space-y-6">
              {schedule.instructions && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Instructions</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {schedule.instructions}
                    </p>
                  </CardContent>
                </Card>
              )}

              {schedule.checklistItems?.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Checklist</h3>
                    <CardDescription>
                      {schedule.checklistItems.length} steps, carried onto every work order this
                      schedule raises
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {schedule.checklistItems.map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {i + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {parts.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Required parts</h3>
                    <CardDescription>
                      Consumed each time this schedule raises a work order
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {parts.map((part) => (
                      <div
                        key={part.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">{part.item?.name}</p>
                          <Link
                            to={`/inventory/${part.itemId}`}
                            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                          >
                            {part.item?.sku}
                          </Link>
                        </div>
                        <span className="shrink-0 font-medium tabular-nums">
                          {part.quantity} {part.unit}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {!schedule.instructions && !schedule.checklistItems?.length && parts.length === 0 && (
                <Card>
                  <CardContent className="py-10 text-center">
                    <p className="text-sm text-muted-foreground">
                      This schedule carries no instructions, checklist or parts, so work orders raised
                      from it will start empty.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>

      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate work order</DialogTitle>
            <DialogDescription>
              This raises a work order from &quot;{schedule.name}&quot; and moves the next due date
              forward.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2 rounded-lg border p-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equipment</span>
                <span className="font-medium">{schedule.equipment?.name ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority</span>
                <StatusBadge status={schedule.priority} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Required parts</span>
                <span className="font-medium tabular-nums">{parts.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next due moves to</span>
                <Badge variant="outline">{interval ? `+${interval}` : frequency}</Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={generateWorkOrder}>Generate work order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
